import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Storage } from '@ionic/storage-angular';

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private _storage: Storage | null = null;
  defaultLang = 'en';

  constructor(private translate: TranslateService, private storage: Storage) {
    this.init();
  }

  async init() {
    console.log('語系初始化');
    this._storage = await this.storage.create();
    // 先檢查是否有儲存的語言資料
    const savedLang = await this.getStoredLanguage();
    if (savedLang) {
      console.log('已有儲存的語言資料 '+savedLang);
      this.setLanguage(savedLang);
    } else {
      // 如果沒有儲存語言資料，則嘗試使用瀏覽器語言
      console.log('如果沒有儲存語言資料，使用瀏覽器語言 '+savedLang);
      const browserLang = this.getBrowserLanguage();
      const lang = browserLang || this.defaultLang;
      this.setLanguage(lang);
    }
  }

  async setLanguage(lang: string) {
    this.translate.use(lang);
    await this._storage?.set('selectedLanguage', lang);
  }

  async getStoredLanguage(): Promise<string | null> {
    return await this._storage?.get('selectedLanguage');
  }

  // 回傳目前語系
  getCurrentLanguage(): string {
    return this.translate.currentLang || this.defaultLang;
  }

    // 取得瀏覽器語言
    getBrowserLanguage(): string | null {
      const browserLang = navigator.language;

      console.log('Browser language:', browserLang);
      if (browserLang) {
        // 如果語言代碼是以 "zh" 開頭，則回傳 "zh-TW"（繁體中文）
        if (browserLang.startsWith('zh')) {
          return 'zh-TW';
        }
        // 如果是 "en"，則回傳 "en"（英文）
        if (browserLang.startsWith('en')) {
          return 'en';
        }
      }
      return 'zh-TW';  // 若無語言或語言不符合條件，回傳 null
    }

  async translateChartData(chartData: any[]): Promise<any[]> {
    // 取得所有翻譯的標題
    const translatedTitles = await Promise.all(
      chartData.map(async (item) => {
        const translatedTitle = await this.translate.get(`titles.${item.id}`).toPromise();
        return { id: item.id, translatedTitle };
      })
    );

    // **計算最長的標題長度**
    const maxLength = Math.max(...translatedTitles.map(item => this.getStringLength(item.translatedTitle)));

    // **補齊標題長度（補在前面）**
    const translatedData = translatedTitles.map(item => ({
      ...chartData.find(data => data.id === item.id),
      title: this.padString(item.translatedTitle, maxLength)
    }));

    return translatedData;
  }

  // **計算字串的顯示長度**（中文字算2個長度，英文字和空格算1個長度）
  getStringLength(text: string): number {
    let length = 0;
    for (let i = 0; i < text.length; i++) {
      const char = text.charAt(i);

      // 如果是中文字符（Unicode 範圍）
      if (char.match(/[\u4e00-\u9fa5]/)) {
        length += 2; // 中文字符算作 2
      }
      // 如果是英文字符或空格（ASCII 範圍）
      else if (char.match(/[\x00-\x7F]/)) {
        length += 1; // 英文字符和空格算作 1
      }
      // 其他符號，視需要決定
      else {
        length += 2; // 如果是特殊符號，這裡假設算作 2
      }
    }
    return length;
  }

  // **補齊字串長度（補在前面）**
  padString(text: string, length: number): string {
    const fullWidthSpace = '\u3000'; // 全形空格
    const halfWidthSpace = '\u0020'; // 半形空格
    const textLength = this.getStringLength(text);

    if (textLength < length) {
      const lang = this.getCurrentLanguage();
      if (lang === 'en') {
        return halfWidthSpace.repeat(length - textLength) + text;  // 英文前面補半形空格
      } else if (lang === 'zh-TW') {
        return fullWidthSpace.repeat((length - textLength) / 2) + text;  // 繁體中文前面補全形空格
      }
    }
    return text;
  }
}
